import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { basketQuantityLimit, availableOrderQuantity } from '../utils/orderAvailability';
import { useBasketStore } from '../store/Basket';
import BasketDesktop from './basket/BasketDesktop';
import BasketMobile from './basket/BasketMobile';
import useBodyScrollLock from '../hooks/useBodyScrollLock';

const MAX_HOUR_IN_BASKET = 16;

const Basket = ({ showModal, close }) => {
  useBodyScrollLock(showModal);
  const products = useBasketStore(state => state.products);
  const patchProduct = useBasketStore(state => state.patchProduct);
  const setOnlyProductTooltip = useBasketStore(state => state.setOnlyProductTooltip);
  const clearAllTooltips = useBasketStore(state => state.clearAllTooltips);
  const deleteProductById = useBasketStore(state => state.deleteProductById);
  const deleteProductsByIds = useBasketStore(state => state.deleteProductsByIds);

  const [allowToOrder, setAllowToOrder] = useState(false);

  useEffect(() => {
    setAllowToOrder(false);
    const token = localStorage.getItem('token');
    if (!showModal || !token) return;
    let cancelled = false;
    axios.get('/api/v1/auth/current-user', { headers: { Authorization: token } })
      .then(response => { if (!cancelled) setAllowToOrder(response.data.data?.type === 'ADMIN'); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [showModal]);

  useEffect(() => {
    const clearVisibleTooltips = () => {
      if (useBasketStore.getState().products.some(product => product.showTooltip)) clearAllTooltips();
    };
    if (!showModal) {
      clearVisibleTooltips();
      return;
    }
    const handleClick = event => {
      if (!event.target.closest('.basket-quantity-plus-wrapper')) clearVisibleTooltips();
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [showModal, clearAllTooltips]);

  useEffect(() => {
    const expiredProductIds = products
      .filter(product => Math.abs(Date.now() - product.dateAdded) / 36e5 > MAX_HOUR_IN_BASKET)
      .map(product => product.id);
    if (expiredProductIds.length > 0) deleteProductsByIds(expiredProductIds);
  }, [products, deleteProductsByIds]);

  useEffect(() => {
    if (!showModal) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showModal, close]);

  const removeProductFromCart = (productId) => {
    deleteProductById(productId);
  };

  const onChangeQuantity = (add, productId) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;
    const resultQuantity = add ? product.quantity + 1 : product.quantity - 1;
    const limit = basketQuantityLimit(product, allowToOrder);

    if (resultQuantity >= 1 && resultQuantity <= limit) {
      patchProduct({ ...product, quantity: resultQuantity, toOrder: resultQuantity > availableOrderQuantity(product), showTooltip: false });
    } else if (resultQuantity > limit) {
      setOnlyProductTooltip(product.id);
    }
  };

  if (!showModal) return null;

  return (
    <>
      <style>{`
        @media (max-width: 991.98px) {
          .basket-modal-header,
          .basket-modal-body,
          .basket-modal-footer {
            padding-left: 0.75rem !important;
            padding-right: 0.75rem !important;
          }
          .basket-modal-body {
            padding-top: 0.25rem !important;
            padding-bottom: 0.25rem !important;
          }
          .basket-modal-container {
            padding-left: 0.5rem !important;
            padding-right: 0.5rem !important;
          }
        }
      `}</style>
      <div
        className="modal-backdrop fade show"
        style={{ opacity: 0.5 }}
        onClick={close}
        onKeyDown={(e) => e.key === 'Escape' && close()}
        role="button"
        tabIndex={-1}
        aria-label="Закрити"
      />
      <div
        className="modal fade show"
        style={{ display: 'block', overflowX: 'hidden', overflowY: 'auto' }}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="basket-title"
        onClick={(e) => { if (e.target === e.currentTarget) close(); }}
      >
        <div
          className="modal-dialog modal-xl"
          role="document"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content">
            <div className="d-none d-lg-block">
              <BasketDesktop
                allowToOrder={allowToOrder}
                products={products}
                onChangeQuantity={onChangeQuantity}
                removeProductFromCart={removeProductFromCart}
                close={close}
              />
            </div>
            <div className="d-lg-none">
              <BasketMobile
                allowToOrder={allowToOrder}
                products={products}
                onChangeQuantity={onChangeQuantity}
                removeProductFromCart={removeProductFromCart}
                close={close}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Basket;
